/**
 * EngineerDetailPanel.jsx - one engineer in full: status header, what the
 * calendar has them on. (The ADP Timesheets tab was removed 10/22/26; the
 * timesheets table keeps its data.)
 *
 * The project shown here is the calendar's, and the Schedule button writes
 * back into it - the same store the grid edits. There is no separate
 * "assigned project" on the engineer row any more: that was a second copy of
 * the same fact, kept in sync by a two-way write from the Projects tab, and
 * the two copies drifted.
 */
import { useState, useMemo } from 'react';
import { Pill } from '../ui/Pill';
import { Icon } from '../ui/Icon';
import { useApp } from '../../context/AppContext';
import { CellEditor } from '../manpower/CellEditor';
import { ymd } from '../manpower/manpowerHelpers';
import { latestDetailFor } from '../manpower/manpowerAssignments';
import { fillCandidates, jobOptions } from '../manpower/projectNumbers';
import { BTN_PRIMARY, headerBg } from './engineerStyles';
import EngineerPhoto from './EngineerPhoto';

function EngineerDetailPanel({ engineer, mp, onBack, onEdit, onDelete, onNavigate, onToast, editable, deletable }) {
  const { allProjects } = useApp();
  const [scheduling, setScheduling] = useState(false);
  const jobs = useMemo(() => jobOptions(allProjects), [allProjects]);
  /* What the calendar has this engineer on today, and the project record
     behind that label if one matches by name. */
  const todayLabel = mp ? mp.projectOn(engineer.name) : '';
  const currentProject = useMemo(
    () => (todayLabel ? allProjects.find((p) => (p.name || '').toLowerCase() === todayLabel.toLowerCase()) : null),
    [todayLabel, allProjects]
  );

  // `detail` is the block's Location / Global Project Number / Status from the
  // editor; it rides along with the label exactly as it does in the calendar.
  const saveSchedule = (from, to, project, detail) => {
    mp.applyEdit(engineer.name, from, to, project, detail);
    if (onToast) onToast(project ? `${engineer.name} scheduled on ${project}` : `${engineer.name}'s schedule cleared`);
  };

  return (
    <div>
      {scheduling && mp && (
        <CellEditor
          initial={{
            engineerName: engineer.name,
            dateFrom: ymd(new Date()),
            dateTo: ymd(new Date()),
            initialCode: todayLabel,
            detail: (todayLabel && mp.detailOn && mp.detailOn(engineer.name)) || null,
          }}
          allProjects={[...new Set([...mp.customProjects, ...Object.values(mp.edits).filter(Boolean)])].sort()}
          colorFor={mp.colorFor}
          suggestDetail={(label) => latestDetailFor(mp.edits, mp.details || {}, label)}
          jobs={jobs}
          fillCandidates={
            mp.fillProjectNumber
              ? (scope, label, from, to) =>
                  fillCandidates(mp.edits, mp.details || {}, { engineerName: engineer.name, from, to, label, scope })
              : undefined
          }
          onFill={mp.fillProjectNumber}
          onSave={saveSchedule}
          onClear={(from, to) => saveSchedule(from, to, '')}
          onClose={() => setScheduling(false)}
        />
      )}
      <div
        onClick={onBack}
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 16, width: 'fit-content' }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
      >
        <div style={{ transform: 'rotate(180deg)', display: 'inline-flex' }}>
          <Icon name="chevron" size={16} color="#555" />
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>Back to Engineers</span>
      </div>

      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          border: '1px solid #ECECEC',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ background: headerBg(engineer.status), padding: '28px', display: 'flex', alignItems: 'center', gap: 20 }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 12,
              overflow: 'hidden',
              background: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <EngineerPhoto
              engineer={engineer}
              fallback={<span style={{ color: '#fff', fontSize: 20, fontWeight: 800 }}>{engineer.initials}</span>}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginBottom: 4 }}>{engineer.name}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>{engineer.title}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Pill label={engineer.status} />
            {editable && (
              <button
                onClick={() => onEdit(engineer)}
                style={{
                  ...BTN_PRIMARY,
                  background: 'rgba(255,255,255,0.15)',
                  padding: '5px 12px',
                  fontSize: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <Icon name="edit" size={10} color="#fff" /> Edit
              </button>
            )}
            {deletable && (
              <button
                onClick={() => onDelete(engineer)}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  color: '#FFCDD2',
                  border: '1px solid rgba(255,200,200,0.3)',
                  padding: '5px 10px',
                  borderRadius: 6,
                  fontSize: 10,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: 'Montserrat',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <Icon name="trash" size={10} color="#FFCDD2" /> Delete
              </button>
            )}
          </div>
        </div>

        <div style={{ padding: '24px 28px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 40px' }}>
            {[
              ['Full Name', engineer.name],
              ['Title', engineer.title],
              ['Skill / Specialty', engineer.skill],
              ['Location', engineer.location],
              ['Status', engineer.status],
              ['Global Project Number', engineer.globalProjectNumber || '-'],
            ].map(([l, v]) => (
              <div
                key={l}
                style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #F5F5F5' }}
              >
                <span style={{ fontSize: 11, color: '#999', fontWeight: 600 }}>{l}</span>
                <span style={{ fontSize: 11, color: '#0D0D0D', fontWeight: 700, textAlign: 'right', maxWidth: '60%' }}>{v}</span>
              </div>
            ))}
          </div>

          {/* What the calendar says - today, straight out of the shared store. */}
          <div style={{ marginTop: 20, padding: '16px 18px', background: '#FAFBFC', borderRadius: 10, border: '1px solid #F0F0F0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#0D0D0D' }}>On the calendar today</div>
              {editable && mp && (
                <button
                  onClick={() => setScheduling(true)}
                  style={{ ...BTN_PRIMARY, padding: '5px 12px', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <Icon name="calendar" size={10} color="#fff" /> Schedule
                </button>
              )}
            </div>
            {todayLabel ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #E8F5E9',
                  background: '#fff',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{ fontSize: 12, fontWeight: 700, color: '#1B5E20', cursor: currentProject ? 'pointer' : 'default' }}
                    onClick={() => currentProject && onNavigate && onNavigate('projects')}
                  >
                    {todayLabel}
                  </div>
                  {currentProject && <div style={{ fontSize: 10, color: '#aaa' }}>{currentProject.customer}</div>}
                </div>
                {currentProject && <Pill label={currentProject.status} />}
              </div>
            ) : (
              <div style={{ fontSize: 11, color: '#aaa', padding: '8px 0' }}>Nothing scheduled for today</div>
            )}
          </div>

          {/* Notes */}
          {engineer.notes && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: '#999', marginBottom: 6 }}>Notes</div>
              <div
                style={{
                  fontSize: 11,
                  color: '#555',
                  lineHeight: 1.6,
                  background: '#FAFBFC',
                  borderRadius: 8,
                  padding: '10px 14px',
                  border: '1px solid #F0F0F0',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {engineer.notes}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EngineerDetailPanel;
